import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Checkbox } from '@/shared/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import {
  FileText,
  Search,
  Filter,
  Calendar,
  Mail,
  FileDown,
  Eye,
  XCircle,
  Edit,
  RefreshCw,
  FileCode,
  CheckCircle2,
  AlertTriangle,
  Clock,
  ChevronLeft,
  ChevronRight,
  Settings,
  Loader2 } from
'lucide-react';
import { SortableHeader, useSortableData } from '@/shared/components/ui/sortable-table';

import { oigAPI } from '@/shared/services/api';

export const NFeList = () => {const { t } = useTranslation("oig");
  const [selectedUnit, setSelectedUnit] = useState('02.892.407/0002-87');
  const [ambiente, setAmbiente] = useState('producao');
  const [selectedItems, setSelectedItems] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [nfeList, setNfeList] = useState([]);
  const [nfeConfig, setNfeConfig] = useState({ companies: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [listData, configData] = await Promise.all([
        oigAPI.getNFeList(),
        oigAPI.getConfig()]
        );

        setNfeList(listData || []);
        setNfeConfig(configData || { companies: [] });
      } catch (err) {
        console.error('Failed to fetch NF-e data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Apply sorting to the list
  const { sortedData, sortConfig, requestSort } = useSortableData(nfeList);

  const filteredList = filterStatus === 'all' ?
  sortedData || [] :
  (sortedData || []).filter((nfe) => (nfe.status || nfe.situacao || '').toLowerCase() === filterStatus);

  const statusColors = {
    'AUTORIZADA': 'bg-emerald-500 text-white',
    'PENDENTE': 'bg-amber-500 text-white',
    'CANCELADA': 'bg-gray-500 text-white',
    'ERRO': 'bg-red-500 text-white',
    'pending': 'bg-amber-500 text-white',
    'completed': 'bg-emerald-500 text-white'
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="oig-nfe-list">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] flex items-center gap-3">
            <FileText className="h-8 w-8 text-emerald-600" />{t("listagemDeNfe", "Listagem de NF-e")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("notasFiscaisEletrnicas", "Notas Fiscais Eletr\xF4nicas de Produto")}

          </p>
        </div>
      </div>

      {/* Alert Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600" />
        <p className="text-sm text-amber-800">
          <span className="font-medium">{t("ateno", "Aten\xE7\xE3o!")}</span>{t("oSistemaEmissor", "O sistema emissor de NF-e est\xE1 no CNPJ:")}
          <span className="font-mono font-bold ml-1">{selectedUnit}</span>{t("eNoAmbiente", "e no ambiente:")}
          <span className="font-bold ml-1 uppercase">{ambiente === 'producao' ? 'PRODUÇÃO' : 'HOMOLOGAÇÃO'}</span>
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-4 items-center">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">{t("unidade", "Unidade")}</label>
            <Select value={selectedUnit} onValueChange={setSelectedUnit}>
              <SelectTrigger className="w-[280px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {nfeConfig.companies && nfeConfig.companies.length > 0 ?
                nfeConfig.companies.map((c) =>
                <SelectItem key={c.id} value={c.cnpj}>
                      {c.cnpj} - {c.identificacao}
                    </SelectItem>
                ) :

                <SelectItem value={selectedUnit}>
                    {selectedUnit}
                  </SelectItem>
                }
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">{t("ambiente", "Ambiente")}</label>
            <div className="flex gap-1">
              <Button
                variant={ambiente === 'homologacao' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAmbiente('homologacao')}
                className={ambiente === 'homologacao' ? 'bg-gray-600' : ''}>{t("homologao", "HOMOLOGA\xC7\xC3O")}


              </Button>
              <Button
                variant={ambiente === 'producao' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAmbiente('producao')}
                className={ambiente === 'producao' ? 'bg-blue-600' : ''}>{t("produo", "PRODU\xC7\xC3O")}


              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Actions */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input placeholder="Filtros..." className="w-64" />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 mt-4">
            <Button variant="outline" size="sm" className="gap-1">
              <Calendar className="h-4 w-4" />{t("eventos", "Eventos")}

            </Button>
            <Button variant="outline" size="sm" className="gap-1">
              <Edit className="h-4 w-4" />{t("cce", "CCe")}

            </Button>
            <Button variant="outline" size="sm" className="gap-1 text-red-600 hover:text-red-700">
              <XCircle className="h-4 w-4" />{t("descartar", "Descartar")}

            </Button>
            <Button variant="outline" size="sm" className="gap-1">
              <Mail className="h-4 w-4" />{t("email", "E-mail")}

            </Button>
            <Button variant="outline" size="sm" className="gap-1 text-red-600">
              <FileDown className="h-4 w-4" />{t("pdf", "PDF")}

            </Button>
            <Button variant="outline" size="sm" className="gap-1 text-emerald-600">
              <FileCode className="h-4 w-4" />{t("xml", "XML")}

            </Button>
            <Button variant="outline" size="sm" className="gap-1">
              <Eye className="h-4 w-4" />{t("preview", "Preview")}

            </Button>
            <Button variant="outline" size="sm" className="gap-1 text-red-600">
              <XCircle className="h-4 w-4" />{t("cancelar", "Cancelar")}

            </Button>
            <Button variant="outline" size="sm" className="gap-1">
              <FileText className="h-4 w-4" />{t("detalhes", "Detalhes")}

            </Button>
            <Button variant="outline" size="sm" className="gap-1 text-amber-600">
              <Settings className="h-4 w-4" />{t("resolver", "Resolver")}

            </Button>
            <Button variant="outline" size="sm" className="gap-1">{t("rec", "Rec")}

            </Button>
            <Button variant="outline" size="sm" className="gap-1 bg-emerald-50">
              <FileText className="h-4 w-4" />{t("gerar2Nota", "Gerar 2\xAA Nota")}

            </Button>
            <Button variant="outline" size="sm" className="gap-1 text-blue-600">
              <RefreshCw className="h-4 w-4" />{t("reenviar", "Reenviar")}

            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="card-elevated">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border sticky top-0">
                <tr>
                  <th className="py-3 px-4 w-10">
                    <Checkbox />
                  </th>
                  <SortableHeader column="geracao" label="Geração" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader column="situacao" label="Situação" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader column="serie" label="Série" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader column="numeroNF" label="N. NF" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader column="codigoNF" label="C. NF" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader column="dataEmissao" label="Data/Hora Emissão" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader column="docDestinatario" label="Doc. Destinatário" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader column="nomeDestinatario" label="Nome Destinatário" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader column="icms" label="ICMS" sortConfig={sortConfig} onSort={requestSort} align="right" />
                  <SortableHeader column="ipi" label="IPI" sortConfig={sortConfig} onSort={requestSort} align="right" />
                  <SortableHeader column="pis" label="PIS" sortConfig={sortConfig} onSort={requestSort} align="right" />
                  <SortableHeader column="cofins" label="COFINS" sortConfig={sortConfig} onSort={requestSort} align="right" />
                  <SortableHeader column="valorTotal" label="Valor Total" sortConfig={sortConfig} onSort={requestSort} align="right" />
                </tr>
              </thead>
              <tbody>
                {filteredList.map((nfe, index) =>
                <tr key={nfe.id} className={`border-b border-border hover:bg-muted/30 ${index % 2 === 0 ? 'bg-white dark:bg-transparent' : 'bg-gray-50 dark:bg-muted/10'}`}>
                    <td className="py-3 px-4">
                      <Checkbox />
                    </td>
                    <td className="py-3 px-4 text-sm">{nfe.geracao}</td>
                    <td className="py-3 px-4">
                      <Badge className={statusColors[nfe.situacao]}>
                        {nfe.situacao}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-sm">{nfe.serie}</td>
                    <td className="py-3 px-4 font-mono text-sm">{nfe.numeroNF}</td>
                    <td className="py-3 px-4 font-mono text-sm">{nfe.codigoNF}</td>
                    <td className="py-3 px-4 text-sm">
                      {new Date(nfe.dataEmissao).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                    </td>
                    <td className="py-3 px-4 font-mono text-sm">{nfe.docDestinatario}</td>
                    <td className="py-3 px-4 text-sm">{nfe.nomeDestinatario}</td>
                    <td className="py-3 px-4 text-right text-sm">{(nfe.icms ?? 0).toFixed(2)}</td>
                    <td className="py-3 px-4 text-right text-sm">{(nfe.ipi ?? 0).toFixed(2)}</td>
                    <td className="py-3 px-4 text-right text-sm">{(nfe.pis ?? 0).toFixed(2)}</td>
                    <td className="py-3 px-4 text-right text-sm">{(nfe.cofins ?? 0).toFixed(2)}</td>
                    <td className="py-3 px-4 text-right font-medium">{formatCurrency(nfe.valorTotal)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-border">
            <p className="text-sm text-muted-foreground">{t("mostrando1", "Mostrando 1 -")}
              {filteredList.length}{t("de", "de")}{filteredList.length}{t("registros", "registros")}
            </p>
            <div className="flex items-center gap-2">
              <Select defaultValue="100">
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">{t("25", "25")}</SelectItem>
                  <SelectItem value="50">{t("50", "50")}</SelectItem>
                  <SelectItem value="100">{t("100", "100")}</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-1">
                <Button variant="outline" size="icon" disabled>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="default" size="sm" className="bg-blue-600">1</Button>
                <Button variant="outline" size="icon" disabled>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>);

};
